// API utility functions for interacting with backend endpoints

interface DetectorScore {
  name: string;
  score: number;
  passed: boolean;
}

interface AnalyzeTextResponse {
  aiScore: number;
  detectorScores?: DetectorScore[];
}

interface HumanizeTextResponse {
  humanizedText: string;
  originalLength: number;
  humanizedLength: number;
}

export const analyzeText = async (text: string): Promise<AnalyzeTextResponse> => {
  try {
    // Use relative URL - works automatically in Vercel
    const response = await fetch('/api/analyze-text', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error analyzing text:', error);
    throw error;
  }
};

export const humanizeText = async (text: string): Promise<HumanizeTextResponse> => {
  try {
    // Use relative URL - works automatically in Vercel
    const response = await fetch('/api/humanize-text', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error humanizing text:', error);
    throw error;
  }
};

// Fallback functions for development/demo purposes
export const analyzeTextDemo = async (text: string): Promise<AnalyzeTextResponse> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 2000));

  // Multi-detector analysis based on real heuristics
  const scores = {
    GPTZero: calculateGPTZeroScore(text),
    Turnitin: calculateTurnitinScore(text),
    ZeroGPT: calculateZeroGPTScore(text),
    Copyleaks: calculateCopyleaksScore(text),
    QuillBot: calculateQuillBotScore(text),
    Grammarly: calculateGrammarlyScore(text)
  };

  // Create detector scores array with pass/fail status
  // Score < 40 = passed (low AI detection)
  // Score 40-60 = warning (medium AI detection)
  // Score > 60 = failed (high AI detection)
  const detectorScores: DetectorScore[] = Object.entries(scores).map(([name, score]) => ({
    name,
    score,
    passed: score < 40
  }));

  // Average all detector scores for final result
  const avgScore = Math.round(
    Object.values(scores).reduce((a, b) => a + b, 0) / Object.keys(scores).length
  );

  return {
    aiScore: Math.min(Math.max(avgScore, 0), 100),
    detectorScores
  };
};

// GPTZero: Focuses on perplexity and burstiness
function calculateGPTZeroScore(text: string): number {
  let score = 50;
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);

  // Perplexity: Check for predictable patterns
  const formalWords = /\b(furthermore|moreover|consequently|therefore|thus|hence|indeed|notably)\b/gi;
  const formalMatches = text.match(formalWords);
  if (formalMatches) score += Math.min(formalMatches.length * 8, 25);

  // Burstiness: Variation in sentence length
  const lengths = sentences.map(s => s.trim().length);
  const avgLength = lengths.reduce((a, b) => a + b, 0) / lengths.length;
  const variance = lengths.reduce((sum, len) => sum + Math.pow(len - avgLength, 2), 0) / lengths.length;
  const stdDev = Math.sqrt(variance);

  // Low variation (burstiness) suggests AI
  if (stdDev < 20) score += 20;
  else if (stdDev < 40) score += 10;
  else score -= 15; // High variation suggests human

  // Personal touch reduces AI score
  if (text.match(/\b(I|my|me|we|our)\b/gi)) score -= 15;
  if (text.match(/[?!]/g)) score -= 10;

  return score;
}

// Turnitin: Perplexity, burstiness, and stylometric analysis
function calculateTurnitinScore(text: string): number {
  let score = 50;
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const words = text.split(/\s+/);

  // Lexical diversity: unique words / total words
  const uniqueWords = new Set(words.map(w => w.toLowerCase()));
  const lexicalDiversity = uniqueWords.size / words.length;

  // Low lexical diversity suggests AI (repetitive)
  if (lexicalDiversity < 0.4) score += 20;
  else if (lexicalDiversity < 0.6) score += 10;
  else score -= 10; // High diversity suggests human

  // Sentence length consistency (AI tends to be uniform)
  const lengths = sentences.map(s => s.trim().split(/\s+/).length);
  const avgLength = lengths.reduce((a, b) => a + b, 0) / lengths.length;
  const isUniform = lengths.every(len => Math.abs(len - avgLength) < 5);
  if (isUniform) score += 15;

  // Formal transitions
  if (text.match(/\b(in conclusion|to summarize|firstly|secondly|lastly)\b/gi)) score += 20;

  // Contractions suggest human
  if (text.match(/n't|'s|'re|'ve|'ll|'d/g)) score -= 15;

  return score;
}

// ZeroGPT: Pattern analysis and sentence structure
function calculateZeroGPTScore(text: string): number {
  let score = 50;
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);

  // Sentence structure regularity
  const startsWithCapital = sentences.filter(s => /^[A-Z]/.test(s.trim())).length;
  const regularityRatio = startsWithCapital / sentences.length;
  if (regularityRatio > 0.95) score += 15; // Too perfect suggests AI

  // Word choice patterns - AI prefers certain formal words
  const aiWords = /\b(utilize|facilitate|optimal|leverage|implement|comprehensive|robust)\b/gi;
  const aiMatches = text.match(aiWords);
  if (aiMatches) score += Math.min(aiMatches.length * 10, 25);

  // Coherence and flow (simulated by checking conjunctions)
  const conjunctions = text.match(/\b(and|but|or|nor|for|yet|so|because|since|unless)\b/gi);
  const conjunctionRatio = conjunctions ? conjunctions.length / sentences.length : 0;
  if (conjunctionRatio > 0.8) score += 10; // Too many suggests AI smoothness

  // Exclamations and questions suggest emotion (human)
  const emotionalMarks = text.match(/[!?]/g);
  if (emotionalMarks && emotionalMarks.length > 2) score -= 15;

  return score;
}

// Copyleaks: Focuses on reverse detection (what's NOT human)
function calculateCopyleaksScore(text: string): number {
  let score = 50;
  const words = text.split(/\s+/);

  // Human writing patterns - informal language
  const informalWords = /\b(kinda|sorta|gonna|wanna|yeah|nope|cool|awesome|terrible)\b/gi;
  const informalMatches = text.match(informalWords);
  if (informalMatches) score -= Math.min(informalMatches.length * 8, 20);

  // AI phrases detection - overly formal or technical
  const aiPhrases = /\b(it is important to note|it should be emphasized|one must consider|in today's world)\b/gi;
  const aiPhraseMatches = text.match(aiPhrases);
  if (aiPhraseMatches) score += Math.min(aiPhraseMatches.length * 15, 30);

  // Statistical pattern: AI avoids extreme adjectives
  const extremeAdjectives = /\b(amazing|terrible|horrible|wonderful|awful|fantastic|dreadful)\b/gi;
  const extremeMatches = text.match(extremeAdjectives);
  if (extremeMatches && extremeMatches.length > 2) score -= 15;

  // First person narrative strongly suggests human
  const firstPerson = text.match(/\b(I think|I feel|I believe|my opinion|personally)\b/gi);
  if (firstPerson) score -= 20;

  return score;
}

// QuillBot: Detects paraphrased and rewritten AI content
function calculateQuillBotScore(text: string): number {
  let score = 50;
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);

  // Uniform sentence length (typical of paraphrased AI)
  const lengths = sentences.map(s => s.trim().length);
  const avgLength = lengths.reduce((a, b) => a + b, 0) / lengths.length;
  const uniformity = lengths.filter(len => Math.abs(len - avgLength) < 15).length / lengths.length;
  if (uniformity > 0.8) score += 20;

  // Generic language and lack of specificity
  const genericPhrases = /\b(very good|quite interesting|somewhat important|rather significant)\b/gi;
  const genericMatches = text.match(genericPhrases);
  if (genericMatches) score += Math.min(genericMatches.length * 12, 25);

  // Repetitive phrasing patterns
  const wordArray = text.toLowerCase().split(/\s+/);
  const trigrams = [];
  for (let i = 0; i < wordArray.length - 2; i++) {
    trigrams.push(`${wordArray[i]} ${wordArray[i+1]} ${wordArray[i+2]}`);
  }
  const uniqueTrigrams = new Set(trigrams);
  const repetition = 1 - (uniqueTrigrams.size / trigrams.length);
  if (repetition > 0.3) score += 15;

  // Human touch indicators
  if (text.match(/\b(honestly|actually|basically|literally)\b/gi)) score -= 10;
  if (text.match(/[!]{2,}|[?]{2,}/g)) score -= 15; // Multiple exclamations/questions

  return score;
}

// Grammarly: Focuses on syntax and language structure
function calculateGrammarlyScore(text: string): number {
  let score = 50;
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);

  // Perfect grammar and punctuation suggests AI
  const grammarErrors = text.match(/\b(would of|could of|should of)\b/gi);
  if (!grammarErrors && sentences.length > 3) score += 10;

  // Syntax patterns - AI prefers subject-verb-object structure
  const complexSentences = text.match(/[,;:]/g);
  const complexityRatio = complexSentences ? complexSentences.length / sentences.length : 0;
  if (complexityRatio < 0.5) score += 15; // Simple structure suggests AI

  // Passive voice (AI overuses it)
  const passiveVoice = text.match(/\b(is|are|was|were|been|being)\s+\w+ed\b/gi);
  if (passiveVoice && passiveVoice.length > sentences.length * 0.3) score += 20;

  // Conversational markers reduce AI score
  if (text.match(/\b(well|so|you know|I mean|like)\b/gi)) score -= 15;

  // Sentence fragments or incomplete thoughts (human)
  const shortSentences = sentences.filter(s => s.trim().split(/\s+/).length < 5);
  if (shortSentences.length > 0) score -= 10;

  return score;
}

export const humanizeTextDemo = async (text: string): Promise<HumanizeTextResponse> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 3000));

  // First, analyze the text to identify which detectors fail
  const analysis = await analyzeTextDemo(text);
  const failedDetectors = analysis.detectorScores?.filter(d => !d.passed).map(d => d.name) || [];

  let humanized = text;

  // Apply transformations based on which detectors failed

  // GPTZero fixes: Add variety, personal touch, emotion
  if (failedDetectors.includes('GPTZero') || failedDetectors.length > 3) {
    humanized = applyGPTZeroFixes(humanized);
  }

  // Turnitin fixes: Increase lexical diversity, add contractions
  if (failedDetectors.includes('Turnitin') || failedDetectors.length > 3) {
    humanized = applyTurnitinFixes(humanized);
  }

  // ZeroGPT fixes: Replace formal AI words, add imperfections
  if (failedDetectors.includes('ZeroGPT') || failedDetectors.length > 3) {
    humanized = applyZeroGPTFixes(humanized);
  }

  // Copyleaks fixes: Add informal language, personal opinions
  if (failedDetectors.includes('Copyleaks') || failedDetectors.length > 3) {
    humanized = applyCopyleaksFixes(humanized);
  }

  // QuillBot fixes: Break uniformity, add specificity
  if (failedDetectors.includes('QuillBot') || failedDetectors.length > 3) {
    humanized = applyQuillBotFixes(humanized);
  }

  // Grammarly fixes: Add conversational markers, vary sentence structure
  if (failedDetectors.includes('Grammarly') || failedDetectors.length > 3) {
    humanized = applyGrammarlyFixes(humanized);
  }

  // Universal improvements
  humanized = applyUniversalHumanization(humanized);

  return {
    humanizedText: humanized,
    originalLength: text.length,
    humanizedLength: humanized.length
  };
};

// GPTZero: Reduce perplexity, increase burstiness
function applyGPTZeroFixes(text: string): string {
  let result = text;

  // Replace formal transition words with casual ones
  result = result
    .replace(/\bfurthermore\b/gi, 'Also')
    .replace(/\bmoreover\b/gi, 'Plus')
    .replace(/\bconsequently\b/gi, 'So')
    .replace(/\btherefore\b/gi, 'that\'s why')
    .replace(/\bthus\b/gi, 'so')
    .replace(/\bhence\b/gi, 'that\'s why')
    .replace(/\bindeed\b/gi, 'actually')
    .replace(/\bnotably\b/gi, 'especially');

  return result;
}

// Turnitin: Increase lexical diversity, add contractions
function applyTurnitinFixes(text: string): string {
  let result = text;

  // Add contractions to reduce formality
  result = result
    .replace(/\bdo not\b/gi, "don't")
    .replace(/\bcannot\b/gi, "can't")
    .replace(/\bwill not\b/gi, "won't")
    .replace(/\bit is\b/gi, "it's")
    .replace(/\bthat is\b/gi, "that's")
    .replace(/\bwould not\b/gi, "wouldn't")
    .replace(/\bcould not\b/gi, "couldn't")
    .replace(/\bshould not\b/gi, "shouldn't")
    .replace(/\bthey are\b/gi, "they're")
    .replace(/\bwe are\b/gi, "we're")
    .replace(/\byou are\b/gi, "you're")
    .replace(/\bI am\b/gi, "I'm");

  // Replace formal conclusions
  result = result
    .replace(/\bin conclusion\b/gi, 'To wrap up')
    .replace(/\bto summarize\b/gi, 'In short')
    .replace(/\bfirstly\b/gi, 'First')
    .replace(/\bsecondly\b/gi, 'Second')
    .replace(/\blastly\b/gi, 'Finally');

  return result;
}

// ZeroGPT: Replace AI-specific formal words
function applyZeroGPTFixes(text: string): string {
  let result = text;

  // Replace typical AI words with natural alternatives
  result = result
    .replace(/\butilize\b/gi, 'use')
    .replace(/\bfacilitate\b/gi, 'help')
    .replace(/\boptimal\b/gi, 'best')
    .replace(/\bleverage\b/gi, 'use')
    .replace(/\bimplement\b/gi, 'add')
    .replace(/\bcomprehensive\b/gi, 'complete')
    .replace(/\brobust\b/gi, 'strong');

  return result;
}

// Copyleaks: Add informal language and personal touch
function applyCopyleaksFixes(text: string): string {
  let result = text;

  // Replace overly formal phrases
  result = result
    .replace(/\bit is important to note that\b/gi, 'Worth mentioning:')
    .replace(/\bit should be emphasized that\b/gi, 'Keep in mind')
    .replace(/\bone must consider\b/gi, 'you should think about')
    .replace(/\bin today's world\b/gi, 'nowadays');

  return result;
}

// QuillBot: Add variety and specificity
function applyQuillBotFixes(text: string): string {
  let result = text;

  // Replace generic phrases with more specific/varied language
  result = result
    .replace(/\bvery good\b/gi, 'excellent')
    .replace(/\bquite interesting\b/gi, 'fascinating')
    .replace(/\bsomewhat important\b/gi, 'significant')
    .replace(/\brather significant\b/gi, 'crucial');

  return result;
}

// Grammarly: Add conversational markers and sentence variety
function applyGrammarlyFixes(text: string): string {
  let result = text;

  // Convert some passive voice to active (simple cases)
  result = result
    .replace(/\bis considered to be\b/gi, 'seems like')
    .replace(/\bwas created by\b/gi, 'came from')
    .replace(/\bcan be seen as\b/gi, 'looks like');

  return result;
}

// Universal humanization: improvements that help all detectors
function applyUniversalHumanization(text: string): string {
  let result = text;

  // Replace "AI" with variations
  result = result
    .replace(/\bAI\b/g, 'artificial intelligence')
    .replace(/\befficient\b/gi, 'effective')
    .replace(/\bAdditionally\b/gi, 'Plus');

  return result;
}